﻿// ---- Global last-pointer tracker (module-scope) ----
let __lastPointer = { x: 0, y: 0, ok: false };

// px: below this the lerp counts as landed and the frame loop stops until the next pointer event
const SETTLE_EPSILON = 0.05;

if (!window.__mfPointerTrackInstalled) {
    window.__mfPointerTrackInstalled = true;
    document.addEventListener(
        "pointermove",
        (e) => {
            __lastPointer = { x: e.clientX, y: e.clientY, ok: true };
        },
        { passive: true }
    );
}

export function createMouseFollower({ follower, target, options }) {
    const state = {
        follower,
        target,
        options: normalizeOptions(options),

        over: false,
        rafId: 0,
        lastT: 0,

        // pointer (client coords)
        px: 0,
        py: 0,
        havePos: false,

        // animated position
        x: 0,
        y: 0,

        // size cache
        fw: 0,
        fh: 0,
        measured: false,
        ro: null,

        // last desired point in current CoordinateSpace coords (after offsets)
        lastDesired: null,

        // the lerp has landed on the desired point; the frame loop idles until a pointer event
        settled: false,

        // text direction of the target, read once instead of on every frame
        dir: "ltr",
    };

    // The stylesheet owns the static defaults (position, left/top, will-change, pointer-events, opacity);
    // this module only sets what varies: the position mode, the transform and the visibility classes.
    follower.classList.add("mf-hidden"); // start hidden until we decide to show

    applyPositioningMode(state);
    cacheDirection(state);
    setupMeasurement(state);

    // Initial measure
    measureFollower(state);

    // Sync hover right now (fix: enabled while pointer already inside target)
    syncOverFromHover(state);

    // If we're already over, seed pointer coords so it can place immediately
    seedPointerIfNeeded(state);

    // Give it an initial position so it can be visible immediately for Always/Manual
    initPosition(state);

    // If we're OverTarget and already hovered, snap to current desired immediately
    if (state.options.showMode === 1 /*OverTarget*/ && state.over && state.havePos) {
        const desired = computeDesired(state, state.options);
        state.lastDesired = desired;
        snapToDesired(state, desired);
    }

    attachHandlers(state);

    // Apply visibility now that we've seeded state
    applyVisibility(state, /*force*/ true);

    requestAnimationFrame(() => {
        follower.classList.add("mf-ready"); // enable transitions after first paint
    });

    return {
        setOptions(newOptions) {
            state.options = normalizeOptions(newOptions);
            applyPositioningMode(state);
            cacheDirection(state);
            setupMeasurement(state);

            // Re-evaluate hover and seed pointer when options change
            syncOverFromHover(state);
            seedPointerIfNeeded(state);

            // If we don't have any pointer pos yet, provide initial placement for Always/Manual
            if (!state.havePos) {
                measureFollower(state);
                initPosition(state);
            } else if (state.lastDesired) {
                // Options may affect anchoring/clamping; snap to keep stable
                snapToDesired(state, state.lastDesired);
            }

            // If we're OverTarget and already hovered, snap based on actual current desired
            if (state.options.showMode === 1 /*OverTarget*/ && state.over && state.havePos) {
                const desired = computeDesired(state, state.options);
                state.lastDesired = desired;
                snapToDesired(state, desired);
            }

            applyVisibility(state, /*force*/ true);
        },

        dispose() {
            detachHandlers(state);
            if (state.ro) {
                try { state.ro.disconnect(); } catch { }
                state.ro = null;
            }
            stopRAF(state);
        },
    };
}

function normalizeOptions(o) {
    return {
        showMode: o.showMode ?? 1,               // 0 Always, 1 OverTarget, 2 Manual
        visible: (o.visible === undefined ? null : o.visible),
        hideBehavior: o.hideBehavior ?? 2,       // 0 Hide, 1 Freeze, 2 Fade

        offsetX: +o.offsetX || 0,
        offsetY: +o.offsetY || 0,
        anchor: o.anchor ?? 8,
        anchorX: (o.anchorX ?? 0.5),
        anchorY: (o.anchorY ?? 0.5),

        followLerp: clamp01(o.followLerp ?? 0),
        maxSpeed: (o.maxSpeed == null ? null : +o.maxSpeed),
        updateMode: o.updateMode ?? 0,           // 0 RAF, 1 PointerMove

        clampToViewport: !!o.clampToViewport,
        clampToTarget: !!o.clampToTarget,
        clampPadding: +o.clampPadding || 0,
        clampPriority: o.clampPriority ?? 0,     // 0 TargetFirst, 1 ViewportFirst

        coordinateSpace: o.coordinateSpace ?? 0, // 0 Viewport, 1 Page, 2 Target
        accountForScroll: !!o.accountForScroll,

        pointerType: o.pointerType ?? 0,         // 0 MouseOnly, 1 PenAndMouse, 2 All

        measureStrategy: o.measureStrategy ?? 2, // 0 OnStart, 1 OnShow, 2 ResizeObserver, 3 Manual
    };
}

function applyPositioningMode(state) {
    const opts = state.options;
    const follower = state.follower;

    // 0 Viewport -> fixed
    // 1 Page -> absolute (document)
    // 2 Target -> absolute (inside target)
    follower.style.position = (opts.coordinateSpace === 0) ? "fixed" : "absolute";
}

// Read once per create/setOptions: getComputedStyle on every frame forced a style recalc for a value that does not change.
function cacheDirection(state) {
    try {
        state.dir = getComputedStyle(state.target).direction === "rtl" ? "rtl" : "ltr";
    } catch {
        state.dir = "ltr";
    }
}

function setupMeasurement(state) {
    state.measured = false;

    if (state.ro) {
        try { state.ro.disconnect(); } catch { }
        state.ro = null;
    }

    if (state.options.measureStrategy === 2 /*ResizeObserver*/) {
        state.ro = new ResizeObserver(() => {
            measureFollower(state);

            // Re-anchor and re-clamp using last desired point to avoid "jump" when size changes
            if (state.lastDesired) {
                snapToDesired(state, state.lastDesired);
            }

            // a follower that measured 0x0 on creation stayed hidden until the next pointer event
            applyVisibility(state);
        });

        state.ro.observe(state.follower);
    }
}

function syncOverFromHover(state) {
    try {
        state.over = state.target.matches(":hover");
    } catch {
        // ignore
    }
}

function seedPointerIfNeeded(state) {
    // If already hovered and we have no coords (no enter event), seed from global tracker
    if (state.over && !state.havePos && __lastPointer.ok) {
        state.px = __lastPointer.x;
        state.py = __lastPointer.y;
        state.havePos = true;
    }
}

function initPosition(state) {
    const opts = state.options;

    // Only do this for Always/Manual (OverTarget stays hidden until hovered)
    if (opts.showMode === 1 /*OverTarget*/) return;

    let x, y;

    if (opts.coordinateSpace === 2 /*Target*/) {
        x = (state.target.clientWidth ?? 0) / 2;
        y = (state.target.clientHeight ?? 0) / 2;
    } else if (opts.coordinateSpace === 1 /*Page*/) {
        x = window.scrollX + window.innerWidth / 2;
        y = window.scrollY + window.innerHeight / 2;
    } else {
        x = window.innerWidth / 2;
        y = window.innerHeight / 2;
    }

    x += opts.offsetX;
    y += opts.offsetY;

    state.havePos = true;
    state.lastDesired = { x, y };

    snapToDesired(state, state.lastDesired);
}

function attachHandlers(state) {
    const t = state.target;

    state._onEnter = (e) => {
        if (!pointerTypeAllowed(state.options.pointerType, e.pointerType)) return;
        state.over = true;
        recordPointer(state, e);

        // For OverTarget, snap BEFORE showing to prevent flash at (0,0)
        if (state.options.showMode === 1 /*OverTarget*/) {
            const desired = computeDesired(state, state.options);
            state.lastDesired = desired;
            snapToDesired(state, desired);
        }

        applyVisibility(state);
        maybeStart(state);
    };

    state._onLeave = () => {
        state.over = false;
        applyVisibility(state);

        if (state.options.showMode === 1 /*OverTarget*/) {
            stopRAF(state);
        }
    };

    state._onMove = (e) => {
        if (!pointerTypeAllowed(state.options.pointerType, e.pointerType)) return;

        const hadPos = state.havePos;

        // If we get pointermove on target, we are over it.
        // This fixes "enabled while already inside -> never shows until re-enter".
        state.over = true;

        recordPointer(state, e);

        // First move while already over (e.g. created while cursor inside target):
        // snap to correct position before showing to prevent flash at (0,0)
        if (!hadPos && state.options.showMode === 1 /*OverTarget*/) {
            const desired = computeDesired(state, state.options);
            state.lastDesired = desired;
            snapToDesired(state, desired);
            applyVisibility(state);
        }

        if (state.options.updateMode === 1 /*PointerMove*/) {
            tick(state, performance.now(), /*single*/ true);
            return;
        }

        maybeStart(state);
    };

    t.addEventListener("pointerenter", state._onEnter, { passive: true });
    t.addEventListener("pointerleave", state._onLeave, { passive: true });
    t.addEventListener("pointermove", state._onMove, { passive: true });
}

function detachHandlers(state) {
    const t = state.target;
    if (!t) return;
    t.removeEventListener("pointerenter", state._onEnter);
    t.removeEventListener("pointerleave", state._onLeave);
    t.removeEventListener("pointermove", state._onMove);
}

function pointerTypeAllowed(mode, pointerType) {
    if (mode === 2) return true;
    if (mode === 1) return pointerType === "mouse" || pointerType === "pen";
    return pointerType === "mouse";
}

function recordPointer(state, e) {
    state.havePos = true;
    state.px = e.clientX;
    state.py = e.clientY;
    state.settled = false;
}

function maybeStart(state) {
    if (!state.over) return;

    if (!state.rafId && state.options.updateMode === 0 /*RAF*/) {
        state.lastT = performance.now();
        state.rafId = requestAnimationFrame((t) => loop(state, t));
    }
}

function loop(state, t) {
    state.rafId = requestAnimationFrame((tt) => loop(state, tt));
    tick(state, t, /*single*/ false);

    if (!state.over && state.options.showMode === 1 /*OverTarget*/) {
        stopRAF(state);
    }

    // the pointer rests and the lerp has landed: no per-frame work until a pointer event restarts the loop
    if (state.settled) {
        stopRAF(state);
    }
}

function stopRAF(state) {
    if (state.rafId) {
        cancelAnimationFrame(state.rafId);
        state.rafId = 0;
    }
}

function tick(state, t, single) {
    if (!state.havePos) return;
    if (!state.over) return; // by design: no tracking outside target

    if (!state.measured) measureFollower(state);

    const opts = state.options;

    const dt = Math.max(0.001, (t - state.lastT) / 1000);
    state.lastT = t;

    const desired = computeDesired(state, opts);
    state.lastDesired = desired;

    const pos = computeAnchoredClampedPos(state, desired);

    let dx = pos.x - state.x;
    let dy = pos.y - state.y;

    if (opts.followLerp <= 0 || single || (Math.abs(dx) < SETTLE_EPSILON && Math.abs(dy) < SETTLE_EPSILON)) {
        state.x = pos.x;
        state.y = pos.y;
        state.settled = true;
    } else {
        state.settled = false;

        if (opts.maxSpeed != null) {
            const maxStep = opts.maxSpeed * dt;
            const dist = Math.hypot(dx, dy);
            if (dist > maxStep && dist > 0.0001) {
                const s = maxStep / dist;
                dx *= s;
                dy *= s;
            }
        }

        state.x += dx * opts.followLerp;
        state.y += dy * opts.followLerp;
    }

    state.follower.style.transform = `translate3d(${state.x}px, ${state.y}px, 0)`;
}

function computeDesired(state, opts) {
    let x = state.px;
    let y = state.py;

    if (opts.coordinateSpace === 1 /*Page*/) {
        if (opts.accountForScroll) {
            x += window.scrollX;
            y += window.scrollY;
        }
    } else if (opts.coordinateSpace === 2 /*Target*/) {
        const r = state.target.getBoundingClientRect();
        x = state.px - r.left;
        y = state.py - r.top;

        if (opts.accountForScroll) {
            x += state.target.scrollLeft || 0;
            y += state.target.scrollTop || 0;
        }
    }

    x += opts.offsetX;
    y += opts.offsetY;

    return { x, y };
}

function computeAnchoredClampedPos(state, desired) {
    const opts = state.options;

    const bounds = computeBounds(state, opts);
    const dir = state.dir ?? "ltr";

    let anchor = resolveAnchor(opts.anchor, dir);

    if ((opts.anchor === 15 || opts.anchor === 16 || opts.anchor === 17) && bounds) {
        anchor = chooseAutoAnchor(state, desired, bounds, dir, opts);
    }

    let pos = anchoredTopLeft(desired.x, desired.y, anchor, state, opts);

    if (bounds) {
        pos = clampRect(pos.x, pos.y, state.fw, state.fh, bounds, opts.clampPadding);
    }

    return pos;
}

function snapToDesired(state, desired) {
    if (!state.measured) measureFollower(state);

    const pos = computeAnchoredClampedPos(state, desired);

    state.x = pos.x;
    state.y = pos.y;
    state.follower.style.transform = `translate3d(${state.x}px, ${state.y}px, 0)`;
}

function computeBounds(state, opts) {
    const wantTarget = opts.clampToTarget;
    const wantViewport = opts.clampToViewport;

    if (!wantTarget && !wantViewport) return null;

    let useTarget = false;
    if (wantTarget && wantViewport) {
        useTarget = (opts.clampPriority === 0 /*TargetFirst*/);
    } else {
        useTarget = wantTarget;
    }

    if (useTarget) {
        if (opts.coordinateSpace === 2 /*Target*/) {
            return { left: 0, top: 0, right: state.target.clientWidth, bottom: state.target.clientHeight };
        }

        const r = state.target.getBoundingClientRect();

        if (opts.coordinateSpace === 1 /*Page*/) {
            return {
                left: r.left + window.scrollX,
                top: r.top + window.scrollY,
                right: r.right + window.scrollX,
                bottom: r.bottom + window.scrollY,
            };
        }

        return { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
    }

    if (opts.coordinateSpace === 1 /*Page*/) {
        return {
            left: window.scrollX,
            top: window.scrollY,
            right: window.scrollX + window.innerWidth,
            bottom: window.scrollY + window.innerHeight,
        };
    }

    return { left: 0, top: 0, right: window.innerWidth, bottom: window.innerHeight };
}

function resolveAnchor(anchor, dir) {
    const isRTL = (dir === "rtl");
    switch (anchor) {
        case 9: return isRTL ? 2 : 0;   // TopStart
        case 10: return isRTL ? 0 : 2;  // TopEnd
        case 11: return isRTL ? 5 : 3;  // MiddleStart
        case 12: return isRTL ? 3 : 5;  // MiddleEnd
        case 13: return isRTL ? 8 : 6;  // BottomStart
        case 14: return isRTL ? 6 : 8;  // BottomEnd
        case 15:
        case 16:
        case 17:
            return 8; // baseline preferred
        default:
            return anchor;
    }
}

function chooseAutoAnchor(state, desired, bounds, dir, opts) {
    const base = 8; // BottomRight baseline
    const candidates = [];
    const add = (a) => candidates.push(a);

    if (opts.anchor === 15 /*Auto*/) {
        add(base);
        add(6); add(8); add(0); add(2);
        add(7); add(1);
    } else if (opts.anchor === 16 /*AutoX*/) {
        add(base);
        add(6);
    } else if (opts.anchor === 17 /*AutoY*/) {
        add(base);
        add(2);
    }

    let best = candidates[0] ?? base;
    let bestOverflow = Infinity;

    for (const a of candidates) {
        const pos = anchoredTopLeft(desired.x, desired.y, a, state, opts);
        const overflow = overflowAmount(pos.x, pos.y, state.fw, state.fh, bounds, opts.clampPadding);
        if (overflow <= 0) return a;
        if (overflow < bestOverflow) {
            bestOverflow = overflow;
            best = a;
        }
    }

    return best;
}

function anchoredTopLeft(x, y, anchor, state, opts) {
    if (opts.anchor === 18 /*Custom*/) {
        return { x: x - state.fw * opts.anchorX, y: y - state.fh * opts.anchorY };
    }

    switch (anchor) {
        case 0: return { x, y };                                   // TopLeft
        case 1: return { x: x - state.fw / 2, y };                 // TopCenter
        case 2: return { x: x - state.fw, y };                     // TopRight
        case 3: return { x, y: y - state.fh / 2 };                 // MiddleLeft
        case 4: return { x: x - state.fw / 2, y: y - state.fh / 2 }; // Center
        case 5: return { x: x - state.fw, y: y - state.fh / 2 };   // MiddleRight
        case 6: return { x, y: y - state.fh };                     // BottomLeft
        case 7: return { x: x - state.fw / 2, y: y - state.fh };   // BottomCenter
        case 8: return { x: x - state.fw, y: y - state.fh };       // BottomRight
        default:
            return { x, y };
    }
}

function clampRect(x, y, w, h, b, pad) {
    const left = b.left + pad;
    const top = b.top + pad;
    const right = b.right - pad;
    const bottom = b.bottom - pad;

    const maxX = right - w;
    const maxY = bottom - h;

    return { x: clamp(x, left, maxX), y: clamp(y, top, maxY) };
}

function overflowAmount(x, y, w, h, b, pad) {
    const left = b.left + pad;
    const top = b.top + pad;
    const right = b.right - pad;
    const bottom = b.bottom - pad;

    let overflow = 0;
    if (x < left) overflow += (left - x);
    if (y < top) overflow += (top - y);
    if (x + w > right) overflow += (x + w - right);
    if (y + h > bottom) overflow += (y + h - bottom);
    return overflow;
}

function measureFollower(state) {
    const r = state.follower.getBoundingClientRect();

    // If the element isn't in the visual tree yet (e.g. Blazor hasn't
    // finished rendering), dimensions will be 0.  Don't mark as measured
    // so that callers know they still need a real measurement.
    if (r.width === 0 && r.height === 0) return;

    state.fw = r.width;
    state.fh = r.height;
    state.measured = true;
}

function applyVisibility(state, force = false) {
    const opts = state.options;
    const el = state.follower;

    let shouldShow = true;

    if (opts.showMode === 1 /*OverTarget*/) shouldShow = state.over && state.havePos && state.measured;
    else if (opts.showMode === 2 /*Manual*/) shouldShow = (opts.visible === true);

    if (opts.showMode === 0 /*Always*/) {
        shouldShow = state.havePos;
    }

    if (shouldShow) {
        el.classList.remove("mf-hidden");
        el.style.display = "";
        return;
    }

    const wantHardHide = (opts.hideBehavior === 0);
    if (wantHardHide) {
        el.classList.add("mf-hidden");
        el.style.display = "none";
    } else {
        el.style.display = "";
        el.classList.add("mf-hidden");
    }
}

function clamp(v, min, max) {
    if (max < min) return min;
    return Math.min(max, Math.max(min, v));
}
function clamp01(v) {
    v = +v || 0;
    return Math.min(1, Math.max(0, v));
}
